import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { CURRENCIES, type Book, type Currency } from "../entities/ledger/types";
import { archiveBook, createBook, restoreBook, updateBook } from "../features/books/api";
import { useActiveBook } from "../features/books/activeBook";
import { useBooks } from "../features/books/useBooks";
import { useDashboard } from "../features/workspace/useDashboard";
import { humanError } from "../shared/api/client";
import { ChipGroup } from "../shared/ui/ChipGroup";
import { ErrorPanel, LoadingPanel } from "../shared/ui/PageState";
import { Modal } from "../shared/ui/Modal";
import { RowMenu } from "../shared/ui/RowMenu";
import { Select } from "../shared/ui/Select";

interface BooksPageProps {
  currency: Currency;
  onNotify: (message: string) => void;
}

// A book is one set of accounts, transactions and plans. Renaming or
// archiving a book never touches a posting: archiving only hides it and
// closes it to new entries.
export function BooksPage({ currency, onNotify }: BooksPageProps) {
  const books = useBooks();
  const dashboard = useDashboard(currency);
  const queryClient = useQueryClient();
  const { bookID, setBookID } = useActiveBook();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", baseCurrency: currency, reportingCurrency: "" });

  const create = useMutation({
    mutationFn: () => createBook({
      name: form.name.trim(),
      baseCurrency: form.baseCurrency,
      reportingCurrency: form.reportingCurrency === "" ? undefined : (form.reportingCurrency as Currency),
    }),
    onSuccess: async (result) => {
      setForm({ name: "", baseCurrency: currency, reportingCurrency: "" });
      setAdding(false);
      await queryClient.invalidateQueries({ queryKey: ["books"] });
      onNotify(`Book "${result.book.name}" created. Switch to it to start recording.`);
    },
  });

  if (!books.data) {
    return (
      <section className="books-page view" aria-labelledby="books-title">
        <div className="page-heading"><div><p className="eyebrow">Books</p><h1 id="books-title">Your sets of books</h1></div></div>
        {books.isPending ? <LoadingPanel message="Loading your books…" /> : null}
        {books.isError ? <ErrorPanel error={books.error} onRetry={() => books.refetch()} what="your books" scope="collection" /> : null}
      </section>
    );
  }
  const all = books.data.books;
  const active = dashboard.data?.book.id ?? bookID;
  const activeCount = all.filter((book) => !book.archived).length;

  return (
    <section className="books-page view" aria-labelledby="books-title">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Books</p>
          <h1 id="books-title">Your sets of books</h1>
          <p className="lede">Each book has its own accounts, transactions and plans. Switching changes what the rest of Ledgerly shows.</p>
        </div>
        <button className="primary-button" type="button" onClick={() => setAdding((value) => !value)}>
          <span>{adding ? "Close" : "New book"}</span><span aria-hidden="true">{adding ? "×" : "＋"}</span>
        </button>
      </div>

      <Modal
        open={adding} onClose={() => setAdding(false)} title="New book"
        subtitle="Each book has its own accounts, transactions and plans."
        footer={<>
          <button className="secondary-button" type="button" onClick={() => setAdding(false)}>Cancel</button>
          <button className="primary-button compact" type="submit" form="new-book-form" disabled={create.isPending}>
            <span>{create.isPending ? "Creating…" : "Create book"}</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        <form id="new-book-form" className="stacked-form" aria-label="New book"
          onSubmit={(event) => { event.preventDefault(); if (form.name.trim()) create.mutate(); }}>
          <label>Name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Household" required autoFocus /></label>
          <div className="field">
            <span className="field-label">Base currency</span>
            <ChipGroup label="Base currency" value={form.baseCurrency}
              onChange={(value) => setForm({ ...form, baseCurrency: value as Currency })}
              options={CURRENCIES.map((value) => ({ value, label: value }))} />
          </div>
          <div className="field">
            <span className="field-label" id="new-book-reporting">Reporting currency <small>optional, defaults to the base</small></span>
            <Select labelledBy="new-book-reporting" value={form.reportingCurrency} placeholder="— same as base —"
              onChange={(reportingCurrency) => setForm({ ...form, reportingCurrency })}
              options={[{ value: "", label: "— same as base —" }, ...CURRENCIES.map((value) => ({ value, label: value }))]} />
          </div>
          <p className="panel-note">The base currency is what the journal is kept in and can never change. Pick it deliberately.</p>
          {create.error ? <p className="form-error" role="alert">{humanError(create.error)}</p> : null}
        </form>
      </Modal>

      <ul className="book-list">
        {all.map((book) => (
          <BookRow
            key={book.id}
            book={book}
            isActive={book.id === active}
            canArchive={activeCount > 1}
            onSwitch={() => { setBookID(book.id); onNotify(`Switched to "${book.name}".`); }}
            onNotify={onNotify}
          />
        ))}
      </ul>
    </section>
  );
}

interface BookRowProps {
  book: Book;
  isActive: boolean;
  canArchive: boolean;
  onSwitch: () => void;
  onNotify: (message: string) => void;
}

function BookRow({ book, isActive, canArchive, onSwitch, onNotify }: BookRowProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(book.name);
  const [reporting, setReporting] = useState(book.reportingCurrency ?? "");
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["books"] });
    await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    await queryClient.invalidateQueries({ queryKey: ["planning"] });
  };
  const save = useMutation({
    mutationFn: () => updateBook(book.id, { name: name.trim(), reportingCurrency: reporting }),
    onSuccess: async (result) => { setEditing(false); await refresh(); onNotify(`"${result.book.name}" updated.`); },
  });
  const archive = useMutation({
    mutationFn: () => (book.archived ? restoreBook(book.id) : archiveBook(book.id)),
    onSuccess: async (result) => { await refresh(); onNotify(book.archived ? `"${result.book.name}" restored.` : `"${result.book.name}" archived.`); },
  });

  return (
    <li className={`book-row ${book.archived ? "archived" : ""} ${isActive ? "active" : ""}`}>
      <div className="book-row-head">
        <div>
          <strong>{book.name}</strong>
          <small>base {book.baseCurrency} · reporting {book.reportingCurrency ?? book.baseCurrency}</small>
        </div>
        <span className="plan-row-status">
          <span className={`status-pill ${isActive ? "status-fulfilled" : "status-planned"}`}>
            {book.archived ? "archived" : isActive ? "active" : "available"}
          </span>
          <RowMenu label={`Actions for ${book.name}`}>
            {!book.archived && !isActive ? <button className="row-menu-item" type="button" onClick={onSwitch}>Switch to this book</button> : null}
            {!book.archived ? <button className="row-menu-item" type="button" onClick={() => setEditing((value) => !value)}>{editing ? "Close rename form" : "Rename and set reporting…"}</button> : null}
            <button
              className="row-menu-item danger"
              type="button"
              disabled={archive.isPending || (!book.archived && (!canArchive || isActive))}
              title={!book.archived && isActive ? "Switch to another book before archiving this one" : undefined}
              onClick={() => {
                if (book.archived || window.confirm(`Archive "${book.name}"? Its history stays readable; it accepts no new entries.`)) archive.mutate();
              }}
            >
              {book.archived ? "Restore this book" : "Archive this book"}
            </button>
          </RowMenu>
        </span>
      </div>
      <Modal
        open={editing} onClose={() => setEditing(false)} title="Rename this book" subtitle={book.name}
        footer={<>
          <button className="secondary-button" type="button" onClick={() => setEditing(false)}>Cancel</button>
          <button className="primary-button compact" type="submit" form={`book-form-${book.id}`} disabled={save.isPending}>
            <span>{save.isPending ? "Saving…" : "Save"}</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        <form id={`book-form-${book.id}`} className="stacked-form"
          onSubmit={(event) => { event.preventDefault(); if (name.trim()) save.mutate(); }}>
          <label>Name<input value={name} onChange={(event) => setName(event.target.value)} required autoFocus /></label>
          <div className="field">
            <span className="field-label" id={`book-reporting-${book.id}`}>Reporting currency</span>
            <Select labelledBy={`book-reporting-${book.id}`} value={reporting}
              placeholder={`reporting = base (${book.baseCurrency})`} onChange={setReporting}
              options={[{ value: "", label: `reporting = base (${book.baseCurrency})` }, ...CURRENCIES.map((value) => ({ value, label: value }))]} />
          </div>
          <p className="panel-note">The base currency is {book.baseCurrency} and cannot change. History stays as recorded.</p>
          {save.error ? <p className="form-error" role="alert">{humanError(save.error)}</p> : null}
        </form>
      </Modal>
      {save.error || archive.error ? <p className="form-error" role="alert">{humanError(save.error ?? archive.error)}</p> : null}
    </li>
  );
}
